import { ChevronLeftIcon, ChevronRightIcon } from '@heroicons/react/24/outline';
import { Dispatch, SetStateAction, useEffect } from 'react';

import { MarkdownMedia } from '@/components/markdown/MarkdownPreview';
import { Modal } from '@/components/Modal';

interface ImageModalProps {
  open: boolean;
  setOpen: (open: boolean) => void;
  imageSrcs: string[];
  imageIndex: number;
  setImageIndex?: Dispatch<SetStateAction<number>>;
  email?: string;
}

export function ImageModal({
  open,
  setOpen,
  imageSrcs,
  imageIndex,
  setImageIndex,
  email,
}: ImageModalProps) {
  useEffect(() => {
    if (!open) return;
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'ArrowLeft') {
        if (setImageIndex) {
          setImageIndex(idx => (idx - 1 + imageSrcs.length) % imageSrcs.length);
        }
      } else if (e.key === 'ArrowRight') {
        if (setImageIndex) {
          setImageIndex(idx => (idx + 1) % imageSrcs.length);
        }
      } else if (e.key === 'Escape') {
        setOpen(false);
      }
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [open, imageSrcs.length]);

  return (
    <Modal
      open={open}
      onClose={() => setOpen(false)}
      size="7xl"
      title="Media"
      className="max-h-auto"
      hideFooter
      hideClose={false}
      closeOnOutsideClick
    >
      <div className="relative flex min-h-[500px] flex-col items-center justify-center">
        <MarkdownMedia
          src={imageSrcs[imageIndex]}
          thumbnail={false}
          email={email}
        />
        {imageSrcs.length > 1 && (
          <>
            <button
              className="bg-chariot-background-primary hover:bg-chariot-background-secondary absolute top-1/2 left-2 -translate-y-1/2 rounded-full p-2 shadow"
              onClick={() =>
                setImageIndex &&
                setImageIndex(
                  idx => (idx - 1 + imageSrcs.length) % imageSrcs.length
                )
              }
              aria-label="Previous image"
            >
              <ChevronLeftIcon className="size-6" />
            </button>
            <button
              className="bg-chariot-background-primary hover:bg-chariot-background-secondary absolute top-1/2 right-2 -translate-y-1/2 rounded-full p-2 shadow"
              onClick={() =>
                setImageIndex &&
                setImageIndex(idx => (idx + 1) % imageSrcs.length)
              }
              aria-label="Next image"
            >
              <ChevronRightIcon className="size-6" />
            </button>
            <div className="text-chariot-text-secondary absolute bottom-2 left-1/2 -translate-x-1/2 text-xs">
              {imageIndex + 1} / {imageSrcs.length}
            </div>
          </>
        )}
      </div>
    </Modal>
  );
}
